import { Link } from "react-router-dom";
import { ArrowUpRight, Quote } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import { TESTIMONIALS, IMG } from "@/data/content";

const PROOF = [
  {
    label: "Operating experience",
    items: [
      "20+ years in global finance and law.",
      "Senior leadership roles across Morgan Stanley, Credit Suisse, and J.P. Morgan.",
    ],
  },
  {
    label: "Decision-making exposure",
    items: [
      "Talent and promotion committees.",
      "Board roles in multiple organisations.",
    ],
  },
  {
    label: "Current advisory",
    items: [
      "Executive coaching and leadership advisory for organisations and individuals.",
      "External lecturer, ESSEC Business School.",
    ],
  },
];

const ORG_COST = [
  "Strong individual performance is not translating into leadership.",
  "Promotion decisions are inconsistent.",
  "High-potential women are not stepping forward, or not being seen.",
  "Leadership pipelines weaken at the point they matter most.",
];

const CREDENTIALS = [
  "Morgan Stanley",
  "Credit Suisse",
  "J.P. Morgan",
  "BCG Platinion",
  "BlackRock",
  "ESSEC Business School",
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="pt-36 lg:pt-44 pb-16 bg-gwa-bg">
        <div className="gwa-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="gwa-eyebrow"><span className="gwa-rule" />About Pascale Lebaillif</p>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="gwa-display text-5xl lg:text-[5.5rem] mt-7 text-gwa-primary leading-[1.02]">
                Twenty years inside the rooms<br />
                where senior decisions <em className="italic font-light text-gwa-purple">are made.</em>
              </h1>
            </Reveal>
            <Reveal delay={220}>
              <p className="mt-10 text-lg text-gwa-muted-fg max-w-2xl leading-relaxed">
                Two decades operating at the level your leaders are stepping into, and being assessed at.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={200}>
              <div className="aspect-[4/5] overflow-hidden bg-gwa-secondary">
                <img src={IMG.hero} alt="Pascale Lebaillif" data-testid="about-founder-image" className="w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHY — single, commercial, sharp */}
      <section className="bg-[#651A75] text-gwa-bg gwa-noise" data-testid="about-why">
        <div className="gwa-container py-24 lg:py-32 relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="gwa-eyebrow-light"><span className="gwa-rule" />Why this work</p>
            </Reveal>
          </div>
          <div className="lg:col-span-8 space-y-8">
            <Reveal>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-[2.85rem] leading-[1.1]">
                I've sat on the committees where careers are decided. I know exactly what separates those who step into leadership from those who remain valued for delivery.
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <p className="font-serif italic text-2xl text-gwa-gold leading-snug max-w-2xl">
                It is not capability. It is visibility, influence, and the ability to operate at the level the role demands.
              </p>
            </Reveal>
            <Reveal delay={200}>
              <p className="text-lg text-gwa-bg/85 leading-[1.7] font-light max-w-2xl">
                I have watched ready, exceptional women get passed over. The cost is not theirs alone. It is a thin pipeline, repeated promotion misfires, and senior roles filled by default rather than design. That is the gap I close.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHY THIS MATTERS FOR YOUR ORGANISATION — bridge */}
      <section className="bg-gwa-ivory border-b border-gwa-border" data-testid="about-org-cost">
        <div className="gwa-container py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="gwa-eyebrow"><span className="gwa-rule" />Why this matters for your organisation</p>
              <h2 className="gwa-display text-4xl lg:text-5xl mt-6 text-gwa-primary leading-[1.05]">
                The pattern I keep seeing.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <ul className="divide-y divide-gwa-border border-y border-gwa-border">
              {ORG_COST.map((line, i) => (
                <Reveal key={line} delay={60 * i} as="li" className="py-5 flex items-start gap-5">
                  <span className="font-serif text-gwa-purple text-xl leading-none mt-1 w-6 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-lg text-gwa-primary leading-snug">{line}</p>
                </Reveal>
              ))}
            </ul>
            <Reveal delay={300}>
              <p className="mt-10 font-serif italic text-2xl lg:text-[1.85rem] text-gwa-purple leading-snug">
                This is the gap I close.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Background — condensed */}
      <section className="gwa-section bg-gwa-bg">
        <div className="gwa-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <p className="gwa-eyebrow"><span className="gwa-rule" />Background</p>
            </Reveal>
          </div>
          <div className="lg:col-span-8 space-y-7 text-lg text-gwa-primary/85 leading-[1.7] font-light max-w-3xl">
            <Reveal>
              <p>
                Pascale Lebaillif spent over two decades in senior roles at Morgan Stanley, Credit Suisse, and J.P. Morgan, leading global teams, running complex programmes, and sitting on talent and promotion committees deciding senior progression.
              </p>
            </Reveal>
            <Reveal delay={80}>
              <p>
                Today she works with organisations facing the same pattern: high-performing women not translating into senior leadership.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Track record — grouped proof */}
      <section className="bg-gwa-ivory border-y border-gwa-border">
        <div className="gwa-container py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <Reveal>
                <p className="gwa-eyebrow"><span className="gwa-rule" />Track record</p>
                <h2 className="gwa-display text-4xl lg:text-5xl mt-6 text-gwa-primary leading-tight">
                  Where the credibility<br />comes from.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-px bg-gwa-border">
              {PROOF.map((g, i) => (
                <Reveal key={g.label} delay={80 * i} className="bg-gwa-ivory p-7 lg:p-8 flex flex-col gap-4">
                  <p className="text-xs uppercase tracking-[0.18em] font-semibold text-gwa-purple">{g.label}</p>
                  <ul className="space-y-3 mt-1">
                    {g.items.map((it) => (
                      <li key={it} className="font-serif text-lg text-gwa-primary leading-snug">{it}</li>
                    ))}
                  </ul>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Logo strip — kept */}
      <section className="bg-gwa-secondary border-b border-gwa-border">
        <div className="gwa-container py-14">
          <p className="gwa-eyebrow text-center mb-8">Operating credibility</p>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4">
            {CREDENTIALS.map((c) => (
              <span key={c} className="font-serif text-xl lg:text-2xl text-gwa-primary/75">{c}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials — outcome-led, short */}
      <section id="testimonials" className="gwa-section bg-gwa-bg scroll-mt-32">
        <div className="gwa-container">
          <Reveal>
            <p className="gwa-eyebrow"><span className="gwa-rule" />Outcomes</p>
            <h2 className="gwa-display text-4xl lg:text-5xl mt-6 text-gwa-primary max-w-3xl">
              Senior women, in their own words.
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.author} delay={50 * (i % 2)}>
                <article className="border-l-2 border-gwa-accent pl-7 py-2" data-testid={`testimonial-${i}`}>
                  <Quote className="w-5 h-5 text-gwa-accent mb-4" />
                  <p className="font-serif italic text-2xl lg:text-[1.65rem] text-gwa-primary leading-snug">
                    “{t.pull}”
                  </p>
                  <div className="mt-6">
                    <p className="font-medium text-gwa-primary">{t.author}</p>
                    <p className="text-sm text-gwa-muted-fg tracking-wide">{t.role}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#651A75] text-gwa-bg gwa-noise">
        <div className="gwa-container py-24 lg:py-32 relative z-10 text-center">
          <Reveal>
            <p className="gwa-eyebrow-light justify-center"><span className="gwa-rule" />Engage</p>
            <h2 className="font-serif text-4xl lg:text-6xl mt-7 leading-[1.05] max-w-4xl mx-auto">
              If this is a pattern you recognise,<br />we should talk.
            </h2>
            <div className="mt-12 flex justify-center">
              <Link to="/contact" data-testid="about-cta-pipeline" className="gwa-btn-primary bg-gwa-bg text-gwa-primary border-gwa-bg hover:bg-gwa-gold hover:border-gwa-gold">
                Discuss your leadership pipeline <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
